import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { ArrowLeft, DownloadSimple, Printer, Copy } from '@phosphor-icons/react';
import { toast } from 'sonner';

/**
 * QRCodePage
 *
 * Site-wide shareable QR code for The Show Master. Producers print this and
 * post it at venues, on flyers, or send it digitally. Scanning the code
 * lands users on the homepage where they can browse shows and enter.
 *
 * The target URL uses window.location.origin so the QR points to whatever
 * environment the producer is currently signed in to (preview vs prod).
 * That keeps the workflow consistent: print a code from the live site,
 * riders scan it to go to the live site.
 */
const QRCodePage = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const targetUrl = typeof window !== 'undefined' ? window.location.origin : 'https://thehorseshowsmaster.com';

  const getCanvas = () => {
    if (!canvasRef.current) return null;
    return canvasRef.current.querySelector('canvas');
  };

  const handleDownload = () => {
    const canvas = getCanvas();
    if (!canvas) { toast.error('Could not capture QR code'); return; }
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = 'show-master-qr.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('QR code downloaded');
  };

  const handlePrint = () => {
    const canvas = getCanvas();
    if (!canvas) { toast.error('Could not capture QR code'); return; }
    const dataUrl = canvas.toDataURL('image/png');
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<!doctype html><html><head><title>Show Master QR Poster</title>
      <meta charset="utf-8" />
      <style>
        @page { margin: 0.5in; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; margin: 0; padding: 48px 32px; text-align: center; color: #111; }
        h1 { font-size: 56px; font-weight: 900; margin: 0 0 12px; letter-spacing: -1px; }
        .sub { font-size: 22px; color: #555; margin: 0 0 36px; }
        img { width: 480px; height: 480px; border: 8px solid #000; border-radius: 16px; padding: 16px; background: #fff; }
        .url { margin-top: 28px; font-size: 22px; font-weight: 600; color: #0066cc; word-break: break-all; }
        .footer { margin-top: 48px; font-size: 14px; color: #888; }
      </style></head><body>
      <h1>SHOW MASTER</h1>
      <p class="sub">Scan to enter shows, view results & manage your season</p>
      <img src="${dataUrl}" alt="QR" />
      <div class="url">${targetUrl}</div>
      <div class="footer">Powered by The Show Master &middot; Print &amp; post anywhere</div>
      <script>window.onload=()=>{window.print()};</script>
      </body></html>`);
    w.document.close();
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(targetUrl);
      toast.success('URL copied to clipboard');
    } catch {
      toast.error('Failed to copy URL');
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#9E9E9E] hover:text-white mb-6 transition-colors"
          data-testid="qr-back-btn"
        >
          <ArrowLeft size={18} /> Back
        </button>

        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-black mb-2" style={{ fontFamily: 'Outfit' }} data-testid="qr-page-title">
            Site QR Code
          </h1>
          <p className="text-[#9E9E9E] text-sm">
            Print, share, or post anywhere — riders scan to land on the home page.
          </p>
        </div>

        {/* QR card */}
        <div
          ref={canvasRef}
          className="bg-white rounded-2xl p-8 mx-auto max-w-md flex flex-col items-center shadow-2xl"
          data-testid="qr-card"
        >
          <QRCodeCanvas
            value={targetUrl}
            size={320}
            bgColor="#ffffff"
            fgColor="#000000"
            level="H"
            includeMargin
          />
          <p className="mt-4 text-xs text-gray-500 break-all text-center" data-testid="qr-target-url">
            {targetUrl}
          </p>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-8 max-w-md mx-auto">
          <button
            onClick={handleDownload}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-[#34C759] hover:bg-[#34C759]/85 text-black rounded-lg text-sm font-bold transition-colors"
            data-testid="qr-download-btn"
          >
            <DownloadSimple size={18} weight="bold" /> Download PNG
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-[#0096FF] hover:bg-[#0096FF]/85 text-white rounded-lg text-sm font-bold transition-colors"
            data-testid="qr-print-btn"
          >
            <Printer size={18} weight="bold" /> Print Poster
          </button>
          <button
            onClick={handleCopyUrl}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-[#161616] border border-[#262626] hover:border-[#FACC15] text-white rounded-lg text-sm font-bold transition-colors"
            data-testid="qr-copy-url-btn"
          >
            <Copy size={18} weight="bold" /> Copy URL
          </button>
        </div>

        <p className="text-center text-[#555] text-xs mt-8 max-w-md mx-auto">
          Tip: download the PNG to drop into Canva, posters, social media, or text it directly to riders.
        </p>
      </div>
    </div>
  );
};

export default QRCodePage;
